import { Injectable } from '@angular/core';
import { BaseHttpService } from './base-http.service';
import { finalize, map, Observable } from 'rxjs';
import { PointTypeDto } from '@shared/interfaces/dtos/points/point-type-dto.interface';
import {
  MailIdDto,
  PointDto,
  PointIdDto,
  PointLinkDto,
  PointLinkedMailDto,
  PointLinkTypeDto,
  PointModelDto,
  PointRecipientDto,
  PointStatusDto,
} from '@shared/interfaces/dtos/points';
import { buildQueryParams, idsQueryParamsBuilder } from '@shared/helpers';
import {
  AddRecipientsToPoint,
  CreateLinkBetweenPointAndMail,
  CreatePointLink,
  DeletePointLink,
  DeletePointMailLink,
  PointAction,
  PointRecipient,
  PointsQuery,
} from '@shared/interfaces/points';
import { ObjectUtils } from '@shared/utils';
import { PointParametersDto } from '@shared/interfaces/dtos/points/point-parameters-dto.interface';
import { PointsMapper } from '@shared/utils/points-mapper.util';

@Injectable({
  providedIn: 'root',
})
export class PointsService extends BaseHttpService {
  protected endPoint: string = 'Points';

  public fetchOnePoint(pointId: number): Observable<PointDto> {
    this.resetEndpoint();

    return this.http.get<PointDto>(this.buildURL({ idOrSubEndpoint: pointId }));
  }

  public fetchPointParameters(): Observable<PointParametersDto> {
    this.endPoint = 'Global/Points/Parameters';

    return this.http.get<PointParametersDto>(this.buildURL()).pipe(finalize(() => this.resetEndpoint()));
  }

  public fetchPointRecipients(pointId: number): Observable<PointRecipientDto[]> {
    return this.http.get<PointRecipientDto[]>(this.buildURL({ idOrSubEndpoint: `${pointId}/Destinataires/Full` }));
  }

  public fetchPointsActionsAndRecipients(
    pointId: number
  ): Observable<{ actions: PointAction[]; recipients: PointRecipient[] }> {
    return this.fetchPointRecipients(pointId).pipe(
      map((res: PointRecipientDto[]) => {
        const actions = res
          .filter((recipient) => !!recipient.action)
          .map((recipient) => PointsMapper.toPointAction(recipient, pointId));
        const recipients = res
          .filter((recipient) => !recipient.action)
          .map((recipient) => PointsMapper.toPointRecipient(recipient));

        return { actions, recipients };
      })
    );
  }

  public fetchPoints(query?: PointsQuery): Observable<PointDto[]> {
    this.resetEndpoint();

    const params = buildQueryParams(query);

    return this.http.get<PointDto[]>(this.buildURL({ idOrSubEndpoint: 'Search' }), { params });
  }

  public fetchPointTypes(): Observable<PointTypeDto[]> {
    return this.http
      .get<PointTypeDto[]>(this.buildURL({ overrideEndPoint: 'Global/TypesPoints' }))
      .pipe(finalize(() => this.resetEndpoint()));
  }

  public fetchPointStatuses(): Observable<PointStatusDto[]> {
    return this.http
      .get<PointStatusDto[]>(this.buildURL({ overrideEndPoint: 'Global/StatutsPoints' }))
      .pipe(finalize(() => this.resetEndpoint()));
  }

  public fetchPointModels(): Observable<PointModelDto[]> {
    return this.http
      .get<PointModelDto[]>(this.buildURL({ overrideEndPoint: 'Global/ModelesPoints' }))
      .pipe(finalize(() => this.resetEndpoint()));
  }

  public fetchPointsSuggestions(pointId: number): Observable<PointIdDto[]> {
    this.resetEndpoint();

    return this.http.get<PointIdDto[]>(this.buildURL({ idOrSubEndpoint: 'SearchNonConfidential' }), {
      params: { id: pointId, pageSize: 10 },
    });
  }

  public fetchMailsSuggestions(mailId: number): Observable<MailIdDto[]> {
    this.resetEndpoint();

    return this.http.get<PointIdDto[]>(this.buildURL({ idOrSubEndpoint: 'SearchNonConfidential' }), {
      params: { id: mailId, pageSize: 10 },
    });
  }

  public fetchPointLinkTypes(): Observable<PointLinkTypeDto[]> {
    return this.http
      .get<PointLinkTypeDto[]>(this.buildURL({ overrideEndPoint: 'Global/PointsLienTypes' }))
      .pipe(finalize(() => this.resetEndpoint()));
  }

  public fetchPointLinks(pointId: number): Observable<PointLinkDto[]> {
    this.resetEndpoint();

    return this.http.get<PointLinkDto[]>(this.buildURL({ idOrSubEndpoint: `${pointId}/Links` }));
  }

  public fetchPointLinkedMails(pointMailsIds: number[]): Observable<PointLinkedMailDto[]> {
    return this.http.get<PointLinkedMailDto[]>(
      this.buildURL({ overrideEndPoint: 'Connecteurs/iDelibe/Mails/SearchBasic', apiScope: 'iCourrier' }),
      {
        params: { ids: idsQueryParamsBuilder(pointMailsIds) },
      }
    );
  }

  public addRecipientsToPoint(pointId: number, body: AddRecipientsToPoint): Observable<unknown> {
    ObjectUtils.removeFalsyEntries(body);

    return this.http.post(this.buildURL({ idOrSubEndpoint: `${pointId}/Users` }), body);
  }

  public deletePointRecipients(pointId: number, recipientIds: number | number[]): Observable<unknown> {
    return this.http.delete(this.buildURL({ idOrSubEndpoint: `${pointId}/Users` }), {
      body: {
        destinataires: Array.isArray(recipientIds)
          ? recipientIds.map((recipientId) => ({ id: recipientId }))
          : [recipientIds].map((recipientId) => ({ id: recipientId })),
      },
    });
  }

  public setPointRecipientAsDefault(pointId: number, recipientId: number): Observable<unknown> {
    return this.http.put(
      this.buildURL({ idOrSubEndpoint: `${pointId}/Destinataires/${recipientId}/SetPrincipal` }),
      {}
    );
  }

  public createLinkBetweenPoints(body: CreatePointLink): Observable<unknown> {
    this.resetEndpoint();

    return this.http.post(this.buildURL({ idOrSubEndpoint: 'Links' }), {
      linkPointId: body.linkedPointId,
      typeLiaison: body.linkType,
      pointId: body.currentPointId,
    });
  }

  public createLinkBetweenPointAndMail(pointId: number, body: CreateLinkBetweenPointAndMail): Observable<unknown> {
    this.resetEndpoint();

    return this.http.post(this.buildURL({ idOrSubEndpoint: `${pointId}/CourrierLinks` }), {
      iCourrierIdsToLink: body.mailIds,
    });
  }

  public deleteLinkBetweenPoints(body: DeletePointLink): Observable<unknown> {
    this.resetEndpoint();

    return this.http.delete(this.buildURL({ idOrSubEndpoint: 'Links' }), {
      body: { linkPointId: body.linkedPointId, typeLiaison: body.linkType, pointId: body.currentPointId },
    });
  }

  public deleteLinkBetweenPointAndMail(pointId: number, body: DeletePointMailLink): Observable<unknown> {
    this.resetEndpoint();

    return this.http.delete(this.buildURL({ idOrSubEndpoint: `${pointId}/CourrierLinks` }), {
      body: { iCourrierIdsToDeleteLink: body.mailIds },
    });
  }

  private resetEndpoint(): void {
    this.endPoint = 'Points';
  }
}
